import React, { lazy, FC, Suspense } from 'react'
import classNames from 'classnames'
import { SearchCardProps } from './types'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { Card, Text } from '@toasttab/shared/src/utils/craftUtils'
import VideoAnimation from '@toasttab/shared/src/components/atoms/VideoAnimation/VideoAnimation'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import { badgeColorType } from '@toasttab/shared/src/components/atoms/Badge/Badge.d'
import { LottieProps } from '@toasttab/shared/src/components/atoms/LottiePlayer/LottiePlayer.d'
import ImageResolutions from '@toasttab/shared/src/components/atoms/Image/ImageResolutions'
import buildImageResolution from '@toasttab/shared/src/utils/Contentful/imageResolution'
const LottiePlayer: FC<LottieProps> = lazy(
  () =>
    import('@toasttab/shared/src/components/atoms/LottiePlayer/LottiePlayer')
)

const SearchCard = ({
  badges,
  badgeColor = 'bg-sky-0',
  badgeTop = false,
  parentType,
  bgColor,
  cardBorderColor,
  cardClickable,
  ctaLabel,
  ctaLink,
  dataTestId = 'SearchCard-1',
  elevation,
  eyebrow,
  featuredImage,
  image,
  imageResolutions,
  imageFill,
  imageRound,
  linkIcons,
  loopAnimation,
  padding,
  sectionDescription,
  sectionHeading,
  slideIsActive,
  textAlignment = 'left'
}: SearchCardProps) => {
  const renderContentImage = () => {
    const imageRoundClass = imageRound
      ? 'rounded-lg lg:rounded-md'
      : 'rounded-t-lg lg:rounded-t-md'
    const imgClasses = classNames(
      image?.length > 0 ? image[0].classes : '',
      imageRoundClass,
      'w-auto mx-auto',
      {
        'object-contain h-[160px] sm:h-[180px]': !imageFill,
        'object-cover object-center h-[160px] sm:h-auto md:h-[180px] w-full':
          imageFill
      }
    )
    if (imageResolutions) {
      let imgResolutions = null
      imgResolutions = {
        imageResolutions: buildImageResolution(
          imageResolutions,
          [310, 259, 685],
          { label: 'Small' }
        )
      }
      if (imgResolutions) {
        return (
          <ImageResolutions
            classes={imgClasses}
            fileWidths={{ desktop: '310px', tablet: '259px', mobile: '685px' }}
            images={imgResolutions}
          />
        )
      }
    } else if (image?.length > 0) {
      switch (image[0].extension) {
        case 'json':
          return (
            <Suspense fallback={null}>
              <LottiePlayer
                classes={imageRoundClass}
                corners={imageRound}
                direction={1}
                hover
                lottieFile={image[0].url}
                previewImage={featuredImage}
              />
            </Suspense>
          )
        case 'mp4':
        case 'webm':
          return (
            <div
              className={`${imageRoundClass} w-full relative overflow-hidden`}
            >
              <VideoAnimation
                animation={image[0]}
                autoPlay
                classes={`${imageRoundClass} w-full h-full object-cover`}
                corners
                loop={loopAnimation}
                previewImage={featuredImage}
              />
            </div>
          )
        default: {
          if (image[0].optimizedImages) {
            return (
              <img
                alt=''
                className={imgClasses}
                src={`${image[0].optimizedImages.srcWebp}`}
              />
            )
          }
          if (image[0].url) {
            return <img alt='' className={imgClasses} src={`${image[0].url}`} />
          }
          return null
        }
      }
    }
  }

  const renderBadges = () => {
    if (badges && badges.length > 0) {
      const badgeComponents = badges.map((badge, index) => (
        <Badge
          badgeColor={badgeColor as badgeColorType}
          classes='mr-2 mb-1'
          key={index}
          text={badge}
          uppercase={true}
        />
      ))
      return <div className='mb-3 flex flex-wrap'>{badgeComponents}</div>
    }
    return null
  }

  const wrapperClasses = classNames(
    'flex flex-col justify-between',
    Card.backgroundColor(bgColor),
    Card.borderColor(cardBorderColor),
    Card.elevation(elevation),
    {
      border: Card.borderColor(cardBorderColor) !== '',
      'hover:shadow-3xl':
        ctaLink && ctaLabel && Card.borderColor(cardBorderColor) !== '',
      'rounded-md': imageRound,
      'rounded-t-md': !imageRound,
      'p-6': !cardClickable
    }
  )
  let tabIndex = 0
  if (slideIsActive !== undefined) {
    tabIndex = slideIsActive ? 0 : -1
  }

  const renderCardContents = () => {
    const containerCss = classNames({
      'grid items-center mb-6 h-[160px] sm:h-[180px]': !imageFill,
      'grid items-center mb-6 h-[160px] sm:h-auto md:h-[180px] overflow-hidden ':
        imageFill,
      'rounded-md': imageRound && imageFill,
      'rounded-t-md': !imageRound && imageFill
    })
    return (
      <>
        <div>
          {badgeTop && parentType ? renderBadges() : null}
          <div className={containerCss}>{renderContentImage()}</div>
          <div
            className={`${Text.alignment(textAlignment)} ${Card.cardPadding(
              padding
            )}`}
          >
            <div>
              {!badgeTop && !parentType ? renderBadges() : null}
              {eyebrow ? (
                <Typography
                  as='p'
                  classes='text-secondary mb-4'
                  variation='default'
                >
                  {eyebrow}
                </Typography>
              ) : null}
              <Typography
                as='h4'
                classes='text-default mb-2 sm:mb-4 !font-normal'
                variation='h4'
              >
                {sectionHeading}
              </Typography>
              {sectionDescription ? (
                <Typography
                  as='p'
                  classes='text-secondary mb-6'
                  variation='default'
                >
                  {sectionDescription}
                </Typography>
              ) : null}
            </div>
          </div>
        </div>
        {ctaLink && ctaLabel ? (
          <div>
            <TextLink
              href={ctaLink}
              icon={linkIcons}
              tabIndex={tabIndex}
              text={ctaLabel}
            />
          </div>
        ) : null}
      </>
    )
  }

  return (
    <div
      className={wrapperClasses}
      data-testid={dataTestId}
      tabIndex={tabIndex}
    >
      {cardClickable ? (
        <a className='p-6 h-full' href={ctaLink}>
          {renderCardContents()}
        </a>
      ) : (
        renderCardContents()
      )}
    </div>
  )
}

export default SearchCard
